import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, ViewStyle, StyleProp } from 'react-native';
import { getTagIconComponent } from '@/constants/admin/tagIcons';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { tagPillColors } from '@/utils/admin/tagColors';

export interface TagPillProps {
  name: string;
  color: string | null;
  icon: string | null;
  size?: 'sm' | 'md';
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const TagPill: React.FC<TagPillProps> = memo(
  ({ name, color, icon, size = 'md', disabled = false, style }) => {
    const { mode, redesign } = useAdminColors();

    const slots = useMemo(
      () => tagPillColors(color, disabled, mode, redesign),
      [color, disabled, mode, redesign],
    );

    const sizeStyle = size === 'sm' ? styles.sm : styles.md;
    const iconSize = size === 'sm' ? 12 : 14;
    const IconComponent = getTagIconComponent(icon);

    return (
      <View
        style={[
          styles.base,
          sizeStyle,
          { backgroundColor: slots.bg, borderColor: slots.border },
          style,
        ]}
      >
        {icon ? (
          IconComponent ? (
            <View style={{ opacity: slots.iconOpacity }}>
              <IconComponent width={iconSize} height={iconSize} color={slots.text} />
            </View>
          ) : (
            <Text
              style={[
                size === 'sm' ? styles.iconSm : styles.iconMd,
                { opacity: slots.iconOpacity },
              ]}
            >
              {icon}
            </Text>
          )
        ) : null}
        <Text
          style={[
            size === 'sm' ? styles.textSm : styles.textMd,
            { color: slots.text },
          ]}
          numberOfLines={1}
        >
          {name}
        </Text>
      </View>
    );
  },
);

TagPill.displayName = 'TagPill';

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    borderWidth: 1,
    alignSelf: 'flex-start',
  },
  sm: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    gap: 6,
  },
  md: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    gap: 7,
  },
  iconSm: { fontSize: 12 },
  iconMd: { fontSize: 14 },
  textSm: { fontSize: 12, fontWeight: '500' },
  textMd: { fontSize: 13, fontWeight: '500' },
});
